import {produce} from 'immer';
import {useSetAtom} from 'jotai';
import {uniqueId} from 'lodash';
import {useEffect, useState} from 'react';
import toast from '@/components/HotToast';
import {Typography} from '@/components/Typography';
import {useGA4WithParams} from '@/hooks/tracking';
import {GeodataPointVars} from '@/map/@types';
import {useQueryRegionByPoint} from '@/map/api';
import {analysisDrawItemAtom, anaysisListAtom} from '@/map/atom';
import {MAX_VISIBLE_ZOOM_METER, MESSAGES} from '@/map/constants';
import {AreaOverlayType, GeoDataType} from '@/map/enums';
import {useVisibleZoomLevel} from '@/map/hooks';
import {PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {getIntlString} from '@/utils';
import {ContextMenuProps} from './index.d';
import {Item} from './StyledComponents';

interface IProps extends ContextMenuProps {}

const LABEL = {
  [AreaOverlayType.CIRCLE]: '반경',
  [AreaOverlayType.POLYGON]: '다각형',
  [AreaOverlayType.EMD]: '단일 행정동',
};

const getErrorMessage = (
  key: string,
  variables?:
    | {
        [key: string]: string;
      }
    | undefined
) => getIntlString(MESSAGES, key, variables);

export const AnalysisMenu = ({coords, clearContextMenu}: IProps) => {
  const isVisibleLevel = useVisibleZoomLevel(MAX_VISIBLE_ZOOM_METER.anaysis);

  const setDrawItem = useSetAtom(analysisDrawItemAtom);
  const setList = useSetAtom(anaysisListAtom);

  const [regionVars, setRegionVar] = useState<GeodataPointVars>();
  const {data, error, isFetching, isFetched} =
    useQueryRegionByPoint(regionVars);

  const tracking = useGA4WithParams();

  const onMenuClick = (type: AreaOverlayType) => {
    if (!coords) {
      return;
    }

    if (!isVisibleLevel) {
      return toast(
        getErrorMessage('area-analytics-zoom-warning', {
          type: `${LABEL[type]} 분석을`,
        })
      );
    }

    if (type === AreaOverlayType.EMD) {
      setRegionVar({lng: coords.x, lat: coords.y, type: GeoDataType.Emd});
      tracking(`analyisis_${AreaOverlayType.EMD}_context`);
      return;
    }

    if (type === AreaOverlayType.CIRCLE) {
      setDrawItem({
        type,
        [type]: {
          center: coords,
        },
      });
      tracking(`analyisis_${AreaOverlayType.CIRCLE}_context`);
    } else if (type === AreaOverlayType.POLYGON) {
      setDrawItem({
        type,
        [type]: {
          point: coords,
        },
      });
      tracking(`analyisis_${AreaOverlayType.POLYGON}_context`);
    }

    clearContextMenu?.();
  };

  useEffect(() => {
    if (data) {
      setList(
        produce((prev) => {
          prev.push({
            type: AreaOverlayType.EMD,
            id: uniqueId(`${AreaOverlayType.EMD}-`),
            [AreaOverlayType.EMD]: data,
          });
        })
      );
      setRegionVar(undefined);
      clearContextMenu?.();
    } else if (isFetched) {
      toast.error('분석 불가 지역입니다.');
      clearContextMenu?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (error) {
      toast.error('분석 불가 지역입니다.');
      clearContextMenu?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <PaddingBox top={8} bottom={8}>
      <Typography variant="medium12" color={palette.textSecondary}>
        분석
      </Typography>
      <PaddingBox top={8} left={2}>
        {Object.entries(LABEL).map(([type, label]) => (
          <Item
            key={type}
            disabled={type === AreaOverlayType.EMD && isFetching}
            onClick={() => onMenuClick(type as AreaOverlayType)}
          >
            {type === AreaOverlayType.EMD && isFetching ? '분석중...' : label}
          </Item>
        ))}
      </PaddingBox>
    </PaddingBox>
  );
};
